"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp, Search } from "lucide-react";
import { Input } from "./input";

import { cn } from "@/utils/tailwind";

// Hook to check archival mode (lazy import to avoid circular deps)
let useArchivalModeHook: (() => { isArchivalMode: boolean }) | null = null;
try {
  const archivalContext = require("@/contexts/ArchivalModeContext");
  useArchivalModeHook = archivalContext.useArchivalMode;
} catch {
  // Context not available
}

// Context for sharing search state
const SelectSearchContext = React.createContext<{
  searchValue: string;
  setSearchValue: (value: string) => void;
}>({
  searchValue: "",
  setSearchValue: () => {},
});

// Wrapper for Select that provides search context
const SelectRoot = SelectPrimitive.Root;

interface SelectProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {
  searchable?: boolean;
}

const Select = ({ children, searchable = true, disabled, ...props }: SelectProps) => {
  const [searchValue, setSearchValue] = React.useState("");
  
  // Check archival mode
  let isArchivalMode = false;
  try {
    if (useArchivalModeHook) {
      const archival = useArchivalModeHook();
      isArchivalMode = archival.isArchivalMode;
    }
  } catch {
    // Context not available
  }
  
  // Disable in archival mode unless explicitly allowed.
  const effectiveDisabled = disabled || (isArchivalMode && !props["data-allow-archival-edit"]);
  const effectiveSearchable = searchable && !isArchivalMode;

  // Create context value object that changes when searchValue changes
  // This ensures React re-renders consumers when searchValue updates
  const contextValue = React.useMemo(
    () => ({ searchValue, setSearchValue }),
    [searchValue]
  );

  return (
    <SelectSearchContext.Provider value={contextValue}>
      <SelectRoot {...props} disabled={effectiveDisabled}>
        {children}
      </SelectRoot>
    </SelectSearchContext.Provider>
  );
};
Select.displayName = "Select";

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/50 hover:bg-black/50 focus:border-[#4da3ff]/60 focus:ring-2 focus:ring-[#4da3ff]/40 focus:ring-offset-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-150 [&>span]:line-clamp-1 backdrop-blur-sm",
      className,
    )}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 text-white",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 text-white",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

interface SelectContentProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  searchable?: boolean;
  searchPlaceholder?: string;
}

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = "popper", searchable = true, searchPlaceholder = "Search...", ...props }, ref) => {
  const { searchValue, setSearchValue } = React.useContext(SelectSearchContext);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const dataState = (props as any)["data-state"];
    if (searchable && dataState === "open") {
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else if (searchable && dataState === "closed") {
      // Clear search when dropdown closes (only if searchable)
      setSearchValue("");
    }
  }, [searchable, (props as any)["data-state"], setSearchValue]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-white/10 bg-black/95 text-white shadow-lg shadow-black/50 backdrop-blur-md",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        {searchable && (
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-white/40" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={handleSearchChange}
                className="h-8 bg-black/20 border-white/20 text-white text-xs pl-7 placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  // Prevent closing dropdown on Escape if search has value
                  if (e.key === "Escape" && searchValue) {
                    e.preventDefault();
                    setSearchValue("");
                  }
                  // Allow Enter key to select first visible item
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Find first visible SelectItem and trigger selection
                    const firstVisibleItem = e.currentTarget.closest('[role="listbox"]')?.querySelector('[role="option"]:not([hidden])');
                    if (firstVisibleItem) {
                      (firstVisibleItem as HTMLElement).click();
                    }
                  }
                }}
              />
            </div>
          </div>
        )}
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pr-2 pl-8 text-sm font-semibold text-white/70", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

// Helper function to extract text from React children
const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === "string") {
    return children;
  }
  if (typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join(" ");
  }
  if (React.isValidElement(children)) {
    if (children.props?.children) {
      return extractTextFromChildren(children.props.children);
    }
    if (children.props?.value) {
      return String(children.props.value);
    }
  }
  return "";
};

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  // Only read from context if we need to filter (when searchable is enabled)
  // For non-searchable selects, we can skip context reading to prevent re-renders
  const context = React.useContext(SelectSearchContext);
  const searchValue = context?.searchValue || "";
  
  // Filter items based on search value - extract all text from children
  // If searchValue is empty (non-searchable), always show
  const itemText = extractTextFromChildren(children);
  const shouldShow = !searchValue || itemText.toLowerCase().includes(searchValue.toLowerCase());
  
  // Use hidden attribute instead of returning null to prevent ref issues
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-pointer items-center rounded-sm py-2 pr-2 pl-8 text-sm text-white outline-none select-none hover:bg-[#4da3ff]/20 focus:bg-[#4da3ff]/30 focus:text-white transition-colors duration-150 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:bg-[#4da3ff]/20",
        !shouldShow && "hidden",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4 text-[#4da3ff]" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("bg-white/10 -mx-1 my-1 h-px", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// Wrapper component that provides search context
interface SearchableSelectProps {
  children: React.ReactNode;
  searchable?: boolean;
}

export function SearchableSelect({ children, searchable = true }: SearchableSelectProps) {
  const [searchValue, setSearchValue] = React.useState("");

  return (
    <SelectSearchContext.Provider value={{ searchValue, setSearchValue }}>
      {children}
    </SelectSearchContext.Provider>
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
